import {
    darken,
    lighten,
    responsiveFontSizes,
} from '@mui/material'
import { createTheme, Theme } from '@mui/material/styles'
import {
    deepPurple,
    grey,
} from '@mui/material/colors'
import { TypographyOptions } from '@mui/material/styles/createTypography'
import { ThemeCreatorCollection, ThemeType } from '../interfaces'
import car from '../Static/cars.jpg'
import selfservicebg from '../Static/selfservicebg.jpg'
import konttibg from '../Static/konttibg.jpg'
import konttibg2 from '../Static/konttibg2.png'
import {OverridesStyleRules} from '@mui/material/styles/overrides'

export const overrides = (backgroundColor: string): OverridesStyleRules => ({
    MuiCssBaseline: {
        styleOverrides: {
            html: {
                scrollBehavior: 'smooth',
                fontSize: 16,
                height: '100%',
            },
            body: {
                backgroundColor,
                height: '100%',
            },
            '#root': {
                height: '100%',
            },
        },
    },
})

const defaultTypography: TypographyOptions = {
    fontFamily: 'sans-serif',
    caption: {
        fontFamily: 'Arial',
        color: grey[900],
    },
    h1: {
        fontSize: '2.75rem',
    },
    h2: {
        fontSize: '2.5rem',
    },
    h3: {
        fontSize: '2.25rem',
    },
    h4: {
        fontSize: '2rem',
    },
    h5: {
        fontSize: '1.75rem',
    },
    h6: {
        fontSize: '1.5rem',
    },
    subtitle1: {
        fontSize: '1.25rem',
    },
    body1: {
        fontSize: '1rem',
    },
}

const selfServiceTypography: TypographyOptions = {
    fontFamily: '"Inter", sans-serif',
    caption: {
        fontFamily: '"Inter", sans-serif',
    },
    h1: {
        fontSize: '5.5rem',
        fontWeight: 700,
    },
    h2: {
        fontSize: '4rem',
        fontWeight: 600,
    },
    h3: {
        fontSize: '2.8rem',
        fontWeight: 600,
    },
    h4: {
        fontSize: '2.2rem',
        fontWeight: 600,
    },
    h5: {
        fontSize: '1.8rem',
        fontWeight: 600,
    },
    h6: {
        fontSize: '1.5rem',
        fontWeight: 600,
    },
    subtitle1: {
        fontSize: '1.5rem',
        lineHeight: 1.5,
    },
    body1: {
        fontSize: '1.2rem',
    },
}

/* Require and allow only same keys used in themes */
type ThemeAsset = Record<keyof ThemeCreatorCollection, ThemeType>

export const themeAssets: ThemeAsset = {
    default: {
        backgroundImage: car,
        backgroundFilter: 'blur(2px) grayscale(100%)',
        transparentPaper: true,
        headerImage: null,
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: deepPurple[700],
                },
                secondary: {
                    main: '#01BB40',
                    contrastText: '#fff'
                },
                background: {
                    default: darken(deepPurple[700], 0.3),
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
    dekra: {
        backgroundColor: '#ffffff',
        backgroundImage: null,
        transparentPaper: true,
        headerImage: null,
        headerBackgroundColor: '#ffffff',
        headerTextColor: '#000000',
        headerStepIconFill: '#007d40',
        headerStepTextColor: '#ffffff',
        footerBackgroundColor: '#000000',
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#007d40',
                },
                secondary: {
                    main: '#007d40',
                    contrastText: '#fff'
                },
                background: {
                    default: '#ffffff',
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
    white: {
        backgroundColor: '#ffffff',
        backgroundImage: null,
        transparentPaper: true,
        headerImage: null,
        headerBackgroundColor: '#ffffff',
        headerTextColor: deepPurple[700],
        headerStepIconFill: deepPurple[700],
        headerStepTextColor: '#ffffff',
        footerBackgroundColor: '#ffffff',
        footerTextColor: deepPurple[700],
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: deepPurple[700],
                },
                secondary: {
                    main: '#01BB40',
                    contrastText: '#ffffff'
                },
                background: {
                    default: '#fff',
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
    kontti: {
        backgroundImage: konttibg,
        backgroundImageOverlay: true,
        backgroundBlendMode: 'multiply',
        backgroundOpacity: 0.7,
        backgroundFilter: undefined,
        transparentPaper: true,
        headerImage: null,
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#733C75',
                },
                secondary: {
                    main: '#01BB40',
                    contrastText: '#fff'
                },
                background: {
                    default: '#733C75',
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
    kontti2: {
        scrollingBackgroundImage: konttibg2,
        backgroundImageOverlay: false,
        backgroundFilter: undefined,
        transparentPaper: true,
        headerImage: null,
        headerBackgroundColor: '#485928',
        footerBackgroundColor: '#485928',
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#435920',
                },
                secondary: {
                    main: '#67893c',
                    contrastText: '#fff'
                },
                background: {
                    default: '#485928',
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
    kovalainen: {
        backgroundColor: '#ffffff',
        backgroundImage: null,
        transparentPaper: true,
        headerImage: null,
        pricesTextActiveColor: '#5a5e59',
        stepLabelTextColor: '#5a5e59',
        secondaryButtonTextColor: '#5a5e59',
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#5a5e59',
                    contrastText: '#e8e731'
                },
                secondary: {
                    main: '#e8e731',
                    contrastText: '#5a5e59'
                },
                background: {
                    default: '#ffffff',
                    paper: grey[50],
                },
                text: {
                    primary: '#5a5e59',
                    secondary: '#5a5e59'
                },
            },
        }),
    },
    pluskatsastus: {
        backgroundColor: '#ffffff',
        backgroundImage: null,
        transparentPaper: true,
        headerImage: null,
        headerBackgroundColor: '#ffffff',
        headerTextColor: '#000000',
        headerStepIconFill: '#c10933',
        headerStepTextColor: '#ffffff',
        footerBackgroundColor: '#748790',
        footerTextColor: '#ffffff',
        pricesTextColor: '#000000',
        pricesMinColor: '#01BB40',
        pricesLowColor: lighten('#01BB40', 0.5),
        priceTagTextColor: '#000000',
        priceTagHeaderColor: '#e0e0e0',
        priceTagHeaderActiveColor: darken('#e0e0e0', 0.1),
        priceTagMainColor: '#c6c6c6',
        priceTagMainActiveColor: darken('#c6c6c6', 0.1),
        infoFormTextColor: '#000000',
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#c10933',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#c10933',
                    contrastText: '#fff'
                },
                background: {
                    default: '#ffffff',
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
    katsastuspark: {
        backgroundColor: '#ffffff',
        backgroundImage: null,
        transparentPaper: true,
        headerImage: null,
        headerBackgroundColor: '#ffffff',
        headerTextColor: '#000000',
        headerStepIconFill: '#3D85CE',
        headerStepTextColor: '#ffffff',
        footerBackgroundColor: '#748790',
        footerTextColor: '#ffffff',
        pricesTextColor: '#000000',
        pricesMinColor: '#01BB40',
        pricesLowColor: lighten('#01BB40', 0.5),
        priceTagTextColor: '#000000',
        priceTagHeaderColor: '#e0e0e0',
        priceTagHeaderActiveColor: darken('#e0e0e0', 0.1),
        priceTagMainColor: '#c6c6c6',
        priceTagMainActiveColor: darken('#c6c6c6', 0.1),
        infoFormTextColor: '#000000',
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#3D85CE',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#3D85CE',
                    contrastText: '#fff'
                },
                background: {
                    default: '#ffffff',
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
    qkatsastus: {
        backgroundColor: '#ffffff',
        backgroundImage: null,
        transparentPaper: true,
        headerImage: null,
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#195861',
                    contrastText: '#f0c11a'
                },
                secondary: {
                    main: '#f0c11a',
                    contrastText: '#195861'
                },
                background: {
                    default: '#ffffff',
                    paper: grey[50],
                },
                text: {
                    primary: '#195861',
                    secondary: '#195861'
                },
            },
        }),
    },
    selfservice: {
        backgroundColor: '#ffffff',
        backgroundImage: selfservicebg,
        backgroundImageOverlay: true,
        backgroundBlendMode: 'multiply',
        backgroundOpacity: 0.8,
        headerBackgroundColor: '#ffffff00',
        headerTextColor: '#ffffff',
        primaryBorderColor: '#195861',
        primaryLinkColor: lighten('#195861', 0.5),
        secondaryBorderColor: '#195861',
        pricesTextColor: '#195861',
        pricesBackgroundColor: '#ffffff',
        pricesMinColor: lighten('#195861', 0.3),
        pricesLowColor: lighten('#195861', 0.8),
        priceTagTextColor: '#195861',
        priceTagHeaderColor: '#ffffff',
        priceTagHeaderActiveColor: darken('#ffffff', 0.1),
        priceTagMainColor: '#ffffff',
        priceTagMainActiveColor: darken('#ffffff', 0.1),
        priceTagRounded: true,
        selfService: true,
        transparentPaper: true,
        headerImage: null,
        createMuiThemeOptions: (options: ThemeType) => ({
            palette: {
                primary: {
                    main: options.primaryMainColor ?? '#ffffff',
                    contrastText: options.primaryContrastTextColor ?? '#195861'
                },
                secondary: {
                    main: options.secondaryMainColor ?? '#ffffff',
                    contrastText: options.secondaryContrastTextColor ?? '#195861'
                },
                error: {
                    main: options.errorMainColor ?? '#ffbb99',
                },
                background: {
                    default: '#195861',
                    paper: '#195861',
                },
                text: {
                    primary: '#ffffff',
                    secondary: '#195861',
                },
            },
        }),
    },
    selfserviceyellow: {
        backgroundColor: '#ffffff',
        backgroundImage: null,
        headerBackgroundColor: '#ffffff00',
        headerTextColor: '#195861',
        primaryButtonColor: '#ffffff',
        primaryButtonTextColor: '#195861',
        primaryBorderColor: '#f0c11a',
        primaryLinkColor: darken('#f0c11a', 0.2),
        secondaryButtonColor: '#f0c11a',
        secondaryButtonTextColor: '#195861',
        secondaryBorderColor: '#f0c11a',
        pricesTextColor: '#195861',
        pricesMinColor: '#f0c11a',
        pricesLowColor: lighten('#f0c11a', 0.5),
        priceTagTextColor: '#195861',
        priceTagHeaderColor: lighten('#f0c11a', 0.25),
        priceTagHeaderActiveColor: '#f0c11a',
        priceTagMainColor: '#f0c11a',
        priceTagMainActiveColor: darken('#f0c11a', 0.1),
        priceTagRounded: true,
        selfService: true,
        transparentPaper: true,
        headerImage: null,
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#195861',
                    contrastText: '#ffffff'
                },
                secondary: {
                    main: '#195861',
                    contrastText: '#f0c11a'
                },
                background: {
                    default: '#ffffff',
                    paper: grey[50],
                },
                text: {
                    primary: '#195861',
                    secondary: '#195861',
                },
            },
        }),
    },
    tehokatsastus: {
        backgroundImage: car,
        backgroundFilter: 'blur(2px) grayscale(100%) opacity(0.5)',
        transparentPaper: true,
        headerImage: null,
        secondaryButtonColor: '#fff',
        secondaryButtonTextColor: '#101820',
        stepLabelTextColor: '#101820',
        pricesTextActiveColor: '#101820',
        createMuiThemeOptions: (_options: ThemeType) => ({
            palette: {
                primary: {
                    main: '#101820',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#fbe122',
                    contrastText: '#101820',
                },
                background: {
                    default: '#fbe122',
                    paper: grey[50],
                },
                text: {
                    primary: grey[900],
                },
            },
        }),
    },
}

export const createAppTheme = (name: string, options: ThemeType): Theme => {
    const theme = themeAssets?.[name] ?? {}
    const muiOptions = theme.createMuiThemeOptions ? theme.createMuiThemeOptions(options) : {}
    const typographyDefaults = (theme.selfService ?? options.selfService) ? selfServiceTypography : defaultTypography

    return responsiveFontSizes(createTheme({
        components: overrides(muiOptions?.palette?.background?.default ?? '#ffffff'),
        typography: {
            ...typographyDefaults,
        },
        ...(muiOptions ?? {})
    }))
}
